import { window } from "global";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import moment from "moment";
import "moment-duration-format";
import { TooltipContainer } from "@outplayed/tooltips";
import MediaQuery from "components/MediaQuery";
import { getProfileUrl } from "lib/profile-helpers";
import {
  calculateKDA,
  getQueueType,
  normalizePatch,
  getKdaColor,
  getPerfScore,
  getSummonerFromTeamDatas,
  hasPerfScore,
} from "lib/general-helper";
import { useLightMode } from "hooks/general-hooks";
import { getRiotAssetsContext } from "@outplayed/riot-assets";

import { CSSTransition, Transition } from "react-transition-group";
import { Item, SummonerSpell, Rune } from "@outplayed/tooltips";
import MultikillIndicator from "./MultikillIndicator";
import MatchSummaryLPDisplay from "./MatchSummaryLPDisplay";
import ItemsList from "components/common/ItemsList";
import { sortTeamByRoles } from "lib/role_helper";
import { ReactComponent as DropdownArrowRed } from "svg/dropdown-arrow-red.svg";
import { ReactComponent as DropdownArrowBlue } from "svg/dropdown-arrow-blue.svg";
import { ReactComponent as DropdownArrowGrey } from "svg/dropdown-arrow-grey.svg";

// import { useTraceUpdate } from "lib/hooks/testingHooks";

const MobileMatchSummary = (props) => {
  const { getChampionImg, getChampionName } = getRiotAssetsContext();
  const { className, data, calculatedProps } = props;
  const { itemList, normalizedPatch } = calculatedProps;

  const expandMatch = (e) => {
    if (!e.outplayed_tooltip_clicked && e.currentTarget.contains(e.target)) {
      props.setIsExpanded(!props.isExpanded);
    }
    e.outplayed_tooltip_clicked = false;
  };

  const matchSummaryClassNames = classnames("match-summary", "match-summary_mobile", props.winConditionClass, className);
  return (
    <div className={matchSummaryClassNames} onClick={expandMatch}>
      {props.isNew && <div className="new-game-indicator" />}
      {/*<div className="decorative-container"/>*/}
      <div className="content-container">
        <div className="match-outcome">
          <div className="flex align-items-center win-lp-stat">
            <div className="victory-status">{calculatedProps.victoryStatus}</div>
            {calculatedProps.isRanked && (
              <MatchSummaryLPDisplay matchLP={props.matchLP} serverLPInfo={props.data.lpInfo} version={data.version} />
            )}
          </div>
          <div className="flex align-items-center queue-type-time">
            <div className="queue-type">{calculatedProps.queueTypeString}</div>
            <div className="from-now">{calculatedProps.fromNow}</div>
          </div>
        </div>
        <div className="match-details">
          <div className="champion-details">
            <div style={{ position: "relative", marginRight: 4 }}>
              <div className="champion-face">
                <img src={getChampionImg(data.championId)} alt={getChampionName(data.championId)} />
              </div>
              <div className="champion-level">{data.level}</div>
            </div>
            <span style={{ fontSize: "16px", color: "red", display: "flex" }}></span>
            <div className="summoner-spells">
              <div className="summoner-spell">{window && <SummonerSpell spellId={data.summonerSpells[0]} />}</div>
              <div className="summoner-spell">{window && <SummonerSpell spellId={data.summonerSpells[1]} />}</div>
            </div>
            <div className="runes">
              <div className="rune">{window && <Rune runeId={calculatedProps.keystone} patch={normalizedPatch} />}</div>
              <div className="rune rune_sub-style">{window && <Rune runeId={data.subStyle} />}</div>
            </div>
          </div>
          <div className="match-stats">
            <div className="align-items-center">
              <div className="multikill">
                <MultikillIndicator multiKillType={calculatedProps.multiKillType} disableText enableTooltip />
              </div>
              <div className="items">
                <ItemsList items={itemList} patch={normalizedPatch} dim={18} winConditionClass={props.winConditionClass} />
              </div>
            </div>
            <div className="align-items-center justify-content-space-between">
              <div className="KDA-totals">
                {data.kills} <span className="slash">/</span> <span className="red">{data.deaths}</span>{" "}
                <span className="slash">/</span> {data.assists}
              </div>
              <div className="KDA-ratio" style={{ color: calculatedProps.kdaColor }}>
                {calculatedProps.kdaRatio} <span className="faded">KDA</span>
              </div>
              <div className="cs">
                {data.cs + data.jungleCs} <span className="faded">CS ({calculatedProps.csPerMinute})</span>
              </div>
              {/*<div className={classnames("expand-match-icon", {"is-expanded": props.isExpanded})}>
                <ChevronDownWithRing />
              </div>*/}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DesktopMatchSummary = (props) => {
  const { getChampionImg, getChampionName, getChampionImgFromSprite } = getRiotAssetsContext();
  const { className, data, calculatedProps } = props;
  const { itemList, normalizedPatch } = calculatedProps;
  const { queueType, lpInfo, matchId, version } = data;
  const summonerTeamData = getSummonerFromTeamDatas(data.summonerName, data.teamA, data.teamB);
  const perfScore = summonerTeamData ? getPerfScore(data.psHardCarry, data.psTeamPlay) : -1;
  const shouldDisplayScoreSection = !props.isBadPerfScore && hasPerfScore(queueType) && perfScore > 0;
  const newTagTimeout = useRef();
  const [newTagNotification, setNewTagNotification] = useState(window && props.isNew);

  useEffect(() => {
    return () => {
      newTagTimeout.current && clearTimeout(newTagTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (newTagNotification) {
      newTagTimeout.current && clearTimeout(newTagTimeout.current);
      newTagTimeout.current = setTimeout(() => {
        setNewTagNotification(false);
      }, 2000);
    }
  }, [newTagNotification]);

  const matchSummaryClassNames = classnames("match-summary", "match-summary_desktop", props.winConditionClass, className);
  //console.log(Math.min(props.maximumKillStreak, 4))
  const win = data.win ? "win" : "loss";

  return (
    <div className={matchSummaryClassNames} onClick={() => props.setIsExpanded(!props.isExpanded)}>
      {props.isNew && <div className="new-game-indicator" />}
      {/*<div className="decorative-container"/>*/}
      {/*new game test button */}
      {/*<button onClick={(e) => {e.stopPropagation(); newTagTimeout.current && clearTimeout(newTagTimeout.current); setNewTagNotification(true);}}>test</button>*/}
      <div className="content-container">
        <div className="group-one">
          <div className="row-one">
            <div className="queue-type">{calculatedProps.queueTypeString}</div>
            <div className="from-now">{calculatedProps.fromNow}</div>
          </div>
          <div className={classnames("row-two", { "row-two_spacing": !newTagNotification && !calculatedProps.isRanked })}>
            <Transition timeout={0} in={newTagNotification} appear={window && newTagNotification}>
              {(state) => (
                <div
                  className={classnames("new-match-notification-container", {
                    [`new-match-notification-container_${state}`]: !calculatedProps.isRanked,
                  })}
                >
                  <Transition timeout={0} in={newTagNotification} appear>
                    {(state) => <div className={`new-match-tag new-match-tag_${state}`}>NEW</div>}
                  </Transition>
                  <Transition timeout={0} in={!newTagNotification} appear={window && newTagNotification}>
                    {(state) =>
                      calculatedProps.isRanked && (
                        <div className={`lp-display_transition lp-display_transition_${state}`}>
                          <MatchSummaryLPDisplay matchLP={props.matchLP} serverLPInfo={lpInfo} version={version} />
                        </div>
                      )
                    }
                  </Transition>
                </div>
              )}
            </Transition>
          </div>
          <div className="row-three">
            <div className="victory-status">{calculatedProps.victoryStatus}</div>&nbsp;
            <div className="game-duration">{calculatedProps.gameDuration}</div>
          </div>
        </div>
        <div className="group-two">
          <div className="row-one">
            <div className="champion">
              <div className="champion-face">
                <img src={getChampionImg(data.championId)} alt={getChampionName(data.championId)} />
                <div className="champion-level">{data.level}</div>
              </div>
            </div>
            <div className="summoner-spells">
              <div className="summoner-spell">{window && <SummonerSpell spellId={data.summonerSpells[0]} />}</div>
              <div className="summoner-spell">{window && <SummonerSpell spellId={data.summonerSpells[1]} />}</div>
            </div>
            <div className="runes">
              <div className="rune">{window && <Rune runeId={calculatedProps.keystone} patch={normalizedPatch} />}</div>
              <div className="rune rune_sub-style">{window && <Rune runeId={data.subStyle} />}</div>
            </div>
          </div>
          {Math.min(calculatedProps.maximumKillStreak, 4) >= 3 ? (
            <div className="multi-kill">
              <MultikillIndicator multiKillType={calculatedProps.multiKillType} />
            </div>
          ) : null}
        </div>
        <div className="post-stats">
          <div className="post-stats-container">
            <div className="KDA-totals">
              {data.kills} <span className={`slash ${win}`}>/</span> <span className="red">{data.deaths}</span>{" "}
              <span className={`slash ${win}`}>/</span> {data.assists}
            </div>
            <div className="KDA-ratio" style={{ color: calculatedProps.kdaColor }}>
              {calculatedProps.kdaRatio} <span className="faded">KDA</span>
            </div>
            <div className="cs">
              {data.cs + data.jungleCs} <span className="faded">CS ({calculatedProps.csPerMinute})</span>
            </div>
            {/*<div className="multi-kill">
            <MultikillIndicator multiKillType={calculatedProps.multiKillType}/>
            </div>*/}
            {!isNaN(data.visionScore) && calculatedProps.showVisionScore && (
              <div className="vision-value">
                <span>{data.visionScore}</span>&nbsp;<span>vision</span>
              </div>
            )}
          </div>
        </div>
        <div className="group-three">
          <div className="items">
            <div className="main-items">
              <ItemsList
                items={itemList.slice(0, itemList.length - 1)}
                patch={normalizedPatch}
                dim={22}
                winConditionClass={props.winConditionClass}
              />
            </div>
            <div className="trinket-icon">
              <ItemsList
                items={[itemList[itemList.length - 1]]}
                patch={normalizedPatch}
                dim={22}
                winConditionClass={props.winConditionClass}
              />
            </div>
          </div>
          {/*
            !isNaN(data.visionScore) && calculatedProps.showVisionScore &&
            <div className="vision-row">
              <div className="vision-icon-container">
                <VisionIcon />
              </div>
              <div className="vision-value">
                <span>Vision:</span>&nbsp;<span>{data.visionScore}</span>
              </div>
            </div>
          */}
        </div>
        <div className="group-four">
          {calculatedProps.teams.map((team, index) => {
            const sortedTeam = sortTeamByRoles(team.slice());
            return (
              <div className="team-list" key={index}>
                {sortedTeam.map(({ championId, summonerName: newSummonerName }, index) => {
                  const isProfileSummonerClass =
                    newSummonerName === data.summonerName ? (data["win"] ? "chosen_win" : "chosen_loss") : "";
                  return (
                    <div className={`summoner-entry ${isProfileSummonerClass}`} key={index}>
                      <div className="champion-face">{getChampionImgFromSprite(championId, { size: 17 })}</div>
                      <div className="summoner-name">
                        <Link
                          title={newSummonerName}
                          to={getProfileUrl("/lol/profile/", data.regionId, newSummonerName, "overview")}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {newSummonerName}
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      <div className={classnames("icons-container", props.winConditionClass, { "is-expanded": props.isExpanded })}>
        <div className={classnames("expand-match-icon", { "is-expanded": props.isExpanded })}>
          {props.winConditionClass === "match_lose" ? (
            <DropdownArrowRed />
          ) : props.winConditionClass === "match_remake" ? (
            <DropdownArrowGrey />
          ) : (
            <DropdownArrowBlue />
          )}
        </div>
      </div>
    </div>
  );
};

const MatchSummary = (props) => {
  const { getRuneKeystoneId } = getRiotAssetsContext();
  const { lightModeOn } = useLightMode();
  const { data, winConditionClass, matchLP, isNew, isExpanded, setIsExpanded, isBadPerfScore } = props;

  if (!data) {
    return null;
  }

  let {
    queueType,
    matchCreationTime,
    win,
    matchDuration,
    championId,
    summonerSpells,
    subStyle,
    primaryStyle,
    version,
    runes,
    assists,
    kills,
    deaths,
    level,
    cs,
    jungleCs,
    items,
    summonerName,
    teamA,
    teamB,
    visionScore,
    killParticipation,
    regionId,
    matchId,
    maximumKillStreak,
    lpInfo,
  } = data;

  const hasHighestPerfScore = useMemo(() => {
    if (isBadPerfScore) {
      return false;
    }

    for (const team of [teamA, teamB]) {
      const currentPlayer = team.find((player) => player.summonerName === summonerName);
      if (currentPlayer !== undefined) {
        let maxScore = 0;
        team.forEach((player) => {
          const avgScore = Math.round(((player.hardCarry || 0) + (player.teamplay || 0)) / 2);
          if (avgScore > maxScore) {
            maxScore = avgScore;
          }
        });

        const currentPlayerAvgScore = Math.round(((currentPlayer.hardCarry || 0) + (currentPlayer.teamplay || 0)) / 2);
        return maxScore !== 0 && currentPlayerAvgScore >= maxScore;
      }
    }
    return false;
  }, [data]);

  const itemList = useMemo(() => {
    var count = 0;
    const list = Array(6).fill(0);
    items.forEach((e, i) => {
      if (e !== 0 && i < items.length - 1) {
        list[count++] = e;
      }
    });
    list.push(items[items.length - 1]);
    return list;
  }, [data]);

  const matchEndTime = matchCreationTime + matchDuration * 1000;
  const remake = matchDuration <= 270;
  const kdaRatio = calculateKDA(kills || 0, deaths || 0, assists || 0);
  const normalizedPatch = normalizePatch(version);
  const calculatedProps = {
    gameDuration: moment.duration(matchDuration, "seconds").format("mm:ss"),
    csPerMinute: Math.round(((cs + jungleCs) / (matchDuration / 60)) * 10) / 10,
    itemList,
    multiKillType: [null, "double", "triple", "quadra", "penta"][Math.min(maximumKillStreak - 1, 4)],
    showVisionScore: !["normal_aram"].includes(queueType),
    remake,
    matchEndTime,
    queueTypeString: getQueueType(queueType),
    isRanked: queueType === "ranked_solo_5x5" || queueType === "ranked_flex_sr",
    fromNow: moment(matchEndTime).locale("match").fromNow(),
    victoryStatus: remake ? "REMAKE" : win ? "WIN" : "LOSS",
    normalizedPatch,
    kdaRatio,
    kdaColor: getKdaColor(kdaRatio, lightModeOn),
    teams: [teamA, teamB],
    keystone: getRuneKeystoneId(runes, { patch: normalizedPatch }),
    hasHighestPerfScore,
    lpInfo: lpInfo,
    maximumKillStreak: maximumKillStreak,
  };

  return (
    <React.Fragment>
      <MediaQuery min="MOBILE_SMALL" max="MOBILE_MEDIUM">
        <MobileMatchSummary {...props} calculatedProps={calculatedProps} />
      </MediaQuery>
      <MediaQuery min="MOBILE_LARGE" max="DESKTOP_LARGE">
        <DesktopMatchSummary {...props} calculatedProps={calculatedProps} />
      </MediaQuery>
    </React.Fragment>
  );
};

export default MatchSummary;
